import {
  ColumnDef,
  flexRender,
  Table as TanstackTable,
} from '@tanstack/react-table';
import _range from 'lodash/range';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { AggregateDataCheckbox } from './aggregate-data-checkbox';
import { Skeleton } from './skeleton';
import { Typography } from './typography';

interface DataTableProps<TData, TValue> {
  table: TanstackTable<TData>;
  columns: ColumnDef<TData, TValue>[];
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  showAggregateData?: boolean;
  setShowAggregateData?: (showAggregateData: boolean) => void;
}

export function DataTable<TData, TValue>({
  table,
  columns,
  isLoading = false,
  emptyState,
  showAggregateData = false,
  setShowAggregateData,
}: DataTableProps<TData, TValue>) {
  const intl = useIntl();

  return (
    <div className="flex flex-col items-start self-stretch">
      <div className="border-fx-neutral-200 flex items-center justify-between self-stretch border-b bg-white p-4">
        <Typography variant="subtitle1">
          {`${table.getRowCount()} `}
          <FormattedMessage defaultMessage="Openings" />
        </Typography>
        {setShowAggregateData && (
          <AggregateDataCheckbox
            checked={showAggregateData}
            onCheckedChange={setShowAggregateData}
            label={intl.formatMessage({
              defaultMessage: 'Show aggregate data',
            })}
          />
        )}
      </div>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <TableHead key={header.id}>
                    <Typography variant="subtitle1">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </Typography>
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            _range(table.getState().pagination.pageSize).map((_, rowIndex) => {
              return (
                <TableRow
                  // eslint-disable-next-line react/no-array-index-key
                  key={`skeleton-${rowIndex}`}
                >
                  {columns.map((_, colIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableCell key={colIndex}>
                      <Skeleton className="h-3 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map(row => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
              >
                {row.getVisibleCells().map(cell => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                {emptyState}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
